import { api } from './api';
import { documentService } from './documentService';

export interface DocumentStatusResponse {
  documentId: string;
  indexedForSearch: boolean;
  indexedWorkspaceIds: string[];
}

export interface DocumentStatusRequest {
  documentId: string;
}

function resolveIndexedWorkspaceIds(data: Record<string, unknown>): string[] {
  if (!Array.isArray(data.indexedWorkspaceIds))
  {
    return [];
  }

  return data.indexedWorkspaceIds.map((id: unknown) => String(id));
}

class DocumentStatusService {

  /**
   * Verifica o status completo de indexação de um documento
   * Retorna tanto o status para busca quanto para chatbot
   */
  async getDocumentStatus(documentId: string): Promise<DocumentStatusResponse> {
    try {
      const response = await api.get(`/chatindoc/index/status/${documentId}`, {
        params: documentService.buildCustomerIdParams(),
      });
      const indexedWorkspaceIds = resolveIndexedWorkspaceIds(response.data);

      return {
        documentId,
        indexedForSearch: response.data.indexedForSearch || false,
        indexedWorkspaceIds,
      };
    } catch (error: any) {
      console.error('❌ Erro ao verificar status do documento:', error);
      
      return {
        documentId,
        indexedForSearch: false,
        indexedWorkspaceIds: []
      };
    }
  }

  /**
   * Verifica se um documento está indexado para busca
   */
  async isDocumentIndexedForSearch(documentId: string): Promise<boolean> {
    try {
      const status = await this.getDocumentStatus(documentId);
      return status.indexedForSearch;
    } catch (error: any) {
      console.error('❌ Erro ao verificar status de busca do documento:', error);
      return false;
    }
  }

  /**
   * Verifica se um documento está indexado para chatbot (via vínculos DIW ativos)
   */
  async isDocumentIndexedForChatbot(documentId: string): Promise<boolean> {
    try {
      const status = await this.getDocumentStatus(documentId);
      return status.indexedWorkspaceIds.length > 0;
    } catch (error: any) {
      console.error('❌ Erro ao verificar status de chatbot do documento:', error);
      return false;
    }
  }

  /**
   * Marca um documento como indexado para busca
   * Nota: Este método apenas atualiza o status local, não faz chamada para a API
   */
  async markDocumentIndexedForSearch(documentId: string): Promise<DocumentStatusResponse> {
    const currentStatus = await this.getDocumentStatus(documentId);
    
    return {
      documentId,
      indexedForSearch: true,
      indexedWorkspaceIds: currentStatus.indexedWorkspaceIds
    };
  }

  /**
   * Remove a marcação de indexação para busca
   * Nota: Este método apenas atualiza o status local, não faz chamada para a API
   */
  async unmarkDocumentIndexedForSearch(documentId: string): Promise<DocumentStatusResponse> {
    const currentStatus = await this.getDocumentStatus(documentId);
    
    return {
      documentId,
      indexedForSearch: false,
      indexedWorkspaceIds: currentStatus.indexedWorkspaceIds
    };
  }
}

export const documentStatusService = new DocumentStatusService();
